import { useState, useCallback, useRef, useEffect } from 'react';
import { AudioService } from '../services/AudioService';
import {
    AudioPlaybackError,
    classifyAudioError,
} from '@eduwrite/shared';

/**
 * State managed by the useAudioPlayback hook.
 */
export interface AudioPlaybackState {
    isLoading: boolean;
    isPlaying: boolean;
    error: AudioPlaybackError | null;
}

/**
 * Return type of the useAudioPlayback hook.
 */
export interface UseAudioPlaybackReturn extends AudioPlaybackState {
    /** Start playback of the audio URL. Sets error state on failure (never swallows). */
    play: () => Promise<void>;
    /** Stop currently playing audio and reset playing state. */
    stop: () => void;
    /** Clear error state and re-attempt playback. */
    retry: () => Promise<void>;
}

/**
 * Shared audio playback hook wrapping AudioService with loading/playing/error
 * state management and retry.
 *
 * On error: classifies as `network | not_found | codec | unknown`, stores in
 * state, and NEVER swallows silently (rules/02 quality standard). The UI must
 * render a visible fallback based on this error state.
 *
 * Platform-agnostic — works via react-native-sound on native and
 * SoundAdapter.web.ts on web.
 *
 * @param url - The audio URL to play.
 * @param onComplete - Optional callback when playback completes successfully.
 */
export function useAudioPlayback(
    url: string,
    onComplete?: () => void,
): UseAudioPlaybackReturn {
    const [state, setState] = useState<AudioPlaybackState>({
        isLoading: false,
        isPlaying: false,
        error: null,
    });

    // Track the current URL and a mounted flag to avoid stale state updates
    const urlRef = useRef(url);
    const mountedRef = useRef(true);
    const onCompleteRef = useRef(onComplete);

    useEffect(() => {
        urlRef.current = url;
        onCompleteRef.current = onComplete;
    }, [url, onComplete]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            AudioService.stop();
        };
    }, []);

    const play = useCallback(async () => {
        const currentUrl = urlRef.current;
        if (!currentUrl) return;

        // Clear any previous error and set loading
        if (mountedRef.current) {
            setState({ isLoading: true, isPlaying: false, error: null });
        }

        try {
            await AudioService.play(currentUrl, () => {
                if (mountedRef.current) {
                    setState({ isLoading: false, isPlaying: false, error: null });
                }
                onCompleteRef.current?.();
            });
            if (mountedRef.current) {
                setState({ isLoading: false, isPlaying: true, error: null });
            }
        } catch (err) {
            // NEVER swallow — classify and surface the error to state
            const classified = classifyAudioError(err);
            if (mountedRef.current) {
                setState({ isLoading: false, isPlaying: false, error: classified });
            }
        }
    }, []);

    const stop = useCallback(() => {
        AudioService.stop();
        if (mountedRef.current) {
            setState({ isLoading: false, isPlaying: false, error: null });
        }
    }, []);

    const retry = useCallback(async () => {
        // Clear error and re-attempt playback
        if (mountedRef.current) {
            setState({ isLoading: false, isPlaying: false, error: null });
        }
        await play();
    }, [play]);

    return {
        isLoading: state.isLoading,
        isPlaying: state.isPlaying,
        error: state.error,
        play,
        stop,
        retry,
    };
}
